import axios from '../modules/AxiosInstance';
import Environment from '../modules/Environment';
import store from '../store';
import UriUtils from '../modules/UriUtils';
import { PAYIT_TENANT_USER_ID, } from '../modules/Constants';
import TokenUtils from '../modules/TokenUtils';

const TransactionService = (() => {
  const service = {};
  const BASE_URL = `${Environment.getBaseURL()}`;

  service.getTransactionsBySearchCriteria = (criteria) => {
    const headers = TokenUtils.getRequestHeaders();

    const queryString = UriUtils.toQueryString(criteria);
    return axios.get(`${BASE_URL}/transaction/search${queryString}`, {
      headers,
    });
  };

  service.getV3TransactionsBySearchCriteria = (criteria) => {
    const { authentication, } = store.getState().auth;
    const headers = {};
    headers.Authorization = authentication.token;
    headers[PAYIT_TENANT_USER_ID] = authentication.tenantUserId;

    const queryString = UriUtils.toQueryString(criteria);
    return axios.get(`${BASE_URL}/v3-transaction/search${queryString}`, {
      headers,
    });
  };

  service.getTransactionsBySearchCriteriaLastFour = (criteria) => {
    const { authentication, } = store.getState().auth;
    const headers = {};
    headers.Authorization = authentication.token;
    headers[PAYIT_TENANT_USER_ID] = authentication.tenantUserId;

    const queryString = UriUtils.toQueryString(criteria);
    return axios.get(`${BASE_URL}/transaction-last-four/search${queryString}`, {
      headers,
    });
  };

  service.getTransactionDetail = (id) => {
    const { authentication, } = store.getState().auth;
    const headers = {};
    headers.Authorization = authentication.token;
    headers[PAYIT_TENANT_USER_ID] = authentication.tenantUserId;

    return axios.get(`${BASE_URL}/transaction/${id}`, {
      headers,
    }).catch((error) => {
      console.warn('Error fetching transaction detail:', error);
      return new Promise((resolve) => {
        resolve({
          transaction: {},
        });
      });
    });
  };

  service.getV3TransactionDetail = (id) => {
    const { authentication, } = store.getState().auth;
    const headers = {};
    headers.Authorization = authentication.token;
    headers[PAYIT_TENANT_USER_ID] = authentication.tenantUserId;

    return axios.get(`${BASE_URL}/v3-transaction/${id}`, {
      headers,
    }).catch((error) => {
      console.warn('Error fetching v3 transaction detail:', error);
      return new Promise((resolve) => {
        resolve({
          v3transaction: {},
        });
      });
    });
  };

  service.fullBillRefund = ({ transactionId, }) => {
    const { authentication, } = store.getState().auth;
    const headers = {};
    headers.Authorization = authentication.token;
    headers[PAYIT_TENANT_USER_ID] = authentication.tenantUserId;

    return axios.post(`${BASE_URL}/transaction/${transactionId}/bill/refund`, undefined, {
      headers,
    });
  };

  service.partialBillRefund = ({ transactionId, billRefundMap, }) => {
    const { authentication, } = store.getState().auth;
    const headers = {};
    headers.Authorization = authentication.token;
    headers[PAYIT_TENANT_USER_ID] = authentication.tenantUserId;

    return axios.post(
      `${BASE_URL}/transaction/${transactionId}/bill/refund/partial`,
      billRefundMap,
      {
        headers,
      }
    );
  };

  service.v3Refund = ({ transactionId, lineItems, fees, }) => {
    const { authentication, } = store.getState().auth;
    const headers = {};
    headers.Authorization = authentication.token;
    headers[PAYIT_TENANT_USER_ID] = authentication.tenantUserId;
    return axios.post(
      `${BASE_URL}/v3/transactions/${transactionId}/bill/refund`,
      {
        lineItems,
        fees,
      },
      {
        headers,
      }
    );
  };

  service.v3RefundServiceRefund = ({
    transactionId,
    lineItems,
    reason,
    note,
    initiatorType,
  }) => {
    const { authentication, } = store.getState().auth;
    const headers = {
      Authorization: authentication.token,
      [PAYIT_TENANT_USER_ID]: authentication.tenantUserId,
    };
    const body = {
      lineItems,
      reason,
      note,
      initiatorType,
      tenantToken: authentication.token,
    };
    return axios.post(
      `${BASE_URL}/v3/transactions/${transactionId}/bill/refund-service-refund`,
      body,
      {
        headers,
      }
    );
  };

  service.addHistoryNote = ({ paymentHistoryId, note, }) => {
    const { authentication, } = store.getState().auth;
    const headers = {};
    headers.Authorization = authentication.token;
    headers[PAYIT_TENANT_USER_ID] = authentication.tenantUserId;
    return axios.post(
      `${BASE_URL}/history/${paymentHistoryId}/note/`,
      { note, },
      {
        headers,
      }
    );
  };

  service.v2FullRefundMoney = (transactionId) => {
    const { authentication, } = store.getState().auth;
    const headers = {};
    headers.Authorization = authentication.token;
    headers[PAYIT_TENANT_USER_ID] = authentication.tenantUserId;
    return axios.post(
      `${BASE_URL}/transactions/${transactionId}/refund`,
      {},
      {
        headers,
      }
    );
  };

  service.v2PartialRefundMoney = (transactionId, refundRequest) => {
    const { authentication, } = store.getState().auth;
    const headers = {};
    headers.Authorization = authentication.token;
    headers[PAYIT_TENANT_USER_ID] = authentication.tenantUserId;
    return axios.post(`${BASE_URL}/transactions/${transactionId}/refund/partial`, refundRequest, {
      headers,
    });
  };

  service.v3FullRefundMoney = (transactionId) => {
    const { authentication, } = store.getState().auth;
    const headers = {};
    headers.Authorization = authentication.token;
    headers[PAYIT_TENANT_USER_ID] = authentication.tenantUserId;
    return axios.post(
      `${BASE_URL}/v3/transactions/${transactionId}/refund`,
      {},
      {
        headers,
      }
    );
  };
  service.v3PartialRefundMoney = (transactionId, refundRequest) => {
    const { authentication, } = store.getState().auth;
    const headers = {};
    headers.Authorization = authentication.token;
    headers[PAYIT_TENANT_USER_ID] = authentication.tenantUserId;
    return axios.post(
      `${BASE_URL}/v3/transactions/${transactionId}/refund/lineitems`,
      refundRequest,
      {
        headers,
      }
    );
  };

  service.getRefundTransactionsByTransactionId = (transactionId) => {
    const { authentication, } = store.getState().auth;
    const headers = {};
    headers.Authorization = authentication.token;
    headers[PAYIT_TENANT_USER_ID] = authentication.tenantUserId;
    return axios.get(
      `${BASE_URL}/v2/transactions/${transactionId}/refunds`,
      {
        headers,
      }
    );
  };

  service.refundPOSTransaction = ({
    transactionId, tenantId, lineItems, externalId,
  }) => {
    const { authentication, } = store.getState().auth;
    const headers = {};
    headers.Authorization = authentication.token;
    headers[PAYIT_TENANT_USER_ID] = authentication.tenantUserId;
    return axios.post(
      `${BASE_URL}/pos/transactions/${transactionId}/refunds`,
      {
        lineItems,
        tenantId,
        externalId,
      },
      {
        headers,
      }
    );
  };

  service.changeSoid = (changeSoidRequest) => {
    const { authentication, } = store.getState().auth;
    const headers = {};
    headers.Authorization = authentication.token;
    headers[PAYIT_TENANT_USER_ID] = authentication.tenantUserId;

    return axios.patch(`${BASE_URL}/change/soid`, { changeSoidRequest, }, { headers, });
  };

  service.getChangeSoidHistory = (transactionId) => {
    const { authentication, } = store.getState().auth;
    const headers = {};
    headers.Authorization = authentication.token;
    headers[PAYIT_TENANT_USER_ID] = authentication.tenantUserId;

    return axios.get(`${BASE_URL}/change/soid/${transactionId}`, { headers, });
  };

  return service;
})();

export default TransactionService;
